import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  TaskyAlert,
  TaskyBadge,
  TaskyButton,
  TaskyDialog,
  TaskyProgress,
  TaskySpinner,
  TaskyTable,
  TaskyTableBody,
  TaskyTableCell,
  TaskyTableHead,
  TaskyTableHeaderCell,
  TaskyTableRow,
  TaskyTabs,
} from '../tasky';
import adminVideoStyleRecipeService, {
  isTrialRunning,
  type VideoStyleTrialCounts,
  type VideoStyleTrialDetail,
  type VideoStyleTrialFilter,
  type VideoStyleTrialListRow,
} from '../../services/api/adminVideoStyleRecipeService';
import VideoStyleTrialPlayer from './VideoStyleTrialPlayer';
import { useVisiblePolling } from './useVisiblePolling';

const STATUS_LABEL: Record<VideoStyleTrialListRow['status'], { label: string; variant: 'info' | 'success' | 'destructive' }> = {
  pending: { label: '待機中', variant: 'info' },
  running: { label: '作成中', variant: 'info' },
  completed: { label: '完成', variant: 'success' },
  failed: { label: '失敗', variant: 'destructive' },
};

const formatDateTime = (iso: string | null) =>
  iso ? new Date(iso).toLocaleString('ja-JP', { year: 'numeric', month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

interface Props {
  /** 件数が変わったら親（タブのバッジ）へ知らせる */
  onCounts: (counts: VideoStyleTrialCounts) => void;
  /** 「スタイルを開く」 */
  onOpenStyle: (styleId: number) => void;
}

/**
 * 管理画面「動画スタイル」の「試作」タブ: 全スタイル横断の試作一覧（新しい順・50件ずつ）。
 * 絞り込みの既定は、作成中があれば「作成中」、無ければ「すべて」。作成中が1件以上ある間だけ5秒ごとに取り直す。
 */
const VideoStyleTrialsTab: React.FC<Props> = ({ onCounts, onOpenStyle }) => {
  const [filter, setFilter] = useState<VideoStyleTrialFilter | null>(null);
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState<VideoStyleTrialListRow[]>([]);
  const [counts, setCounts] = useState<VideoStyleTrialCounts>({ running: 0, failed: 0, all: 0 });
  const [lastPage, setLastPage] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<VideoStyleTrialDetail | null>(null);
  const fetched = useRef<string | null>(null);

  const fetchPage = useCallback(
    async (status: VideoStyleTrialFilter, pageNo: number) => {
      try {
        const result = await adminVideoStyleRecipeService.trialList({ status, page: pageNo });
        setRows(result.trials);
        setCounts(result.counts);
        setLastPage(result.lastPage);
        setError(null);
        setIsLoading(false);
        onCounts(result.counts);
        return result;
      } catch {
        setError('試作の一覧を読み込めませんでした。');
        setIsLoading(false);
        return null;
      }
    },
    [onCounts],
  );

  // 初回: 作成中から取り、無ければ「すべて」に切り替える
  useEffect(() => {
    let cancelled = false;
    void fetchPage('running', 1).then((result) => {
      if (cancelled) return;
      const running = result !== null && result.counts.running > 0;
      // 「作成中」に決まったら今取った結果をそのまま使う（同じ一覧を取り直さない）
      fetched.current = running ? 'running:1' : null;
      setFilter(running ? 'running' : 'all');
    });
    return () => {
      cancelled = true;
    };
  }, [fetchPage]);

  // 絞り込み・ページが決まる／変わるたびに取る
  useEffect(() => {
    if (filter === null) return;
    if (fetched.current === `${filter}:${page}`) {
      fetched.current = null;
      return;
    }
    void fetchPage(filter, page);
  }, [filter, page, fetchPage]);

  useVisiblePolling(filter !== null && counts.running > 0, () => fetchPage(filter ?? 'all', page));

  const changeFilter = (value: VideoStyleTrialFilter) => {
    setPage(1);
    setFilter(value);
  };

  const open = async (row: VideoStyleTrialListRow) => {
    try {
      setSelected(await adminVideoStyleRecipeService.trial(row.id));
    } catch {
      setError('試作の詳細を読み込めませんでした。');
    }
  };

  return (
    <div className="flex flex-col gap-3" data-testid="video-style-trials-tab">
      <TaskyTabs
        variant="pill"
        size="sm"
        aria-label="試作の絞り込み"
        value={filter ?? 'running'}
        onChange={(value) => changeFilter(value as VideoStyleTrialFilter)}
        items={[
          { value: 'running', label: '作成中', count: counts.running },
          { value: 'all', label: 'すべて', count: counts.all },
          { value: 'failed', label: '失敗', count: counts.failed },
        ]}
      />

      {error && <TaskyAlert tone="error">{error}</TaskyAlert>}

      {isLoading && rows.length === 0 ? (
        <TaskySpinner />
      ) : rows.length === 0 ? (
        <p className="py-8 text-center text-sm text-gray-500">{filter === 'failed' ? '失敗した試作はありません。' : filter === 'running' ? '作成中の試作はありません。' : 'まだ試作はありません。'}</p>
      ) : (
        <div className="overflow-x-auto">
          <TaskyTable>
            <TaskyTableHead>
              <TaskyTableRow>
                <TaskyTableHeaderCell className="whitespace-nowrap">状態</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>スタイル</TaskyTableHeaderCell>
                <TaskyTableHeaderCell>商品・組織</TaskyTableHeaderCell>
                <TaskyTableHeaderCell className="whitespace-nowrap">作った管理者</TaskyTableHeaderCell>
                <TaskyTableHeaderCell className="whitespace-nowrap">作成日時</TaskyTableHeaderCell>
                <TaskyTableHeaderCell className="whitespace-nowrap">消費相当</TaskyTableHeaderCell>
                <TaskyTableHeaderCell className="whitespace-nowrap">尺</TaskyTableHeaderCell>
                <TaskyTableHeaderCell className="w-16" />
              </TaskyTableRow>
            </TaskyTableHead>
            <TaskyTableBody>
              {rows.map((row) => {
                const status = STATUS_LABEL[row.status];
                return (
                  <TaskyTableRow key={row.id} data-testid="video-style-trials-row">
                    <TaskyTableCell className="min-w-[8rem]">
                      <TaskyBadge variant={status.variant} dot={isTrialRunning(row)}>
                        {status.label}
                      </TaskyBadge>
                      {isTrialRunning(row) ? (
                        <TaskyProgress value={row.progress} className="mt-1 w-28" label={`${row.progress}%`} />
                      ) : (
                        <p className="mt-1 text-xs text-gray-500">{row.progress}%</p>
                      )}
                    </TaskyTableCell>
                    <TaskyTableCell>
                      <p className="font-medium text-dark">{row.style_name ?? '—'}</p>
                      {row.style_status && <TaskyBadge variant={row.style_status === 'published' ? 'success' : 'secondary'}>{row.style_status === 'published' ? '公開' : '下書き'}</TaskyBadge>}
                    </TaskyTableCell>
                    <TaskyTableCell>
                      <p className="text-dark">{row.product_name ?? `商品 #${row.product_id}`}</p>
                      <p className="text-xs text-gray-500">{row.organization_name ?? '—'}</p>
                    </TaskyTableCell>
                    <TaskyTableCell className="whitespace-nowrap">{row.admin_name ?? '—'}</TaskyTableCell>
                    <TaskyTableCell className="whitespace-nowrap">{formatDateTime(row.created_at)}</TaskyTableCell>
                    <TaskyTableCell className="whitespace-nowrap">
                      {row.trial_credits_used.toLocaleString()}
                      <span className="text-xs text-gray-500"> / {row.trial_credits_estimated !== null ? row.trial_credits_estimated.toLocaleString() : '—'}</span>
                    </TaskyTableCell>
                    <TaskyTableCell className="whitespace-nowrap">{row.output_seconds ? `${row.output_seconds.toFixed(1)}秒` : '—'}</TaskyTableCell>
                    <TaskyTableCell>
                      <TaskyButton size="sm" variant="ghost" onClick={() => void open(row)} aria-label={`試作 #${row.id} を開く`}>
                        開く
                      </TaskyButton>
                    </TaskyTableCell>
                  </TaskyTableRow>
                );
              })}
            </TaskyTableBody>
          </TaskyTable>
        </div>
      )}

      {lastPage > 1 && (
        <div className="flex items-center justify-end gap-2 text-sm">
          <TaskyButton size="sm" variant="ghost" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            前へ
          </TaskyButton>
          <span>
            {page} / {lastPage}
          </span>
          <TaskyButton size="sm" variant="ghost" disabled={page >= lastPage} onClick={() => setPage(page + 1)}>
            次へ
          </TaskyButton>
        </div>
      )}

      <TaskyDialog
        open={selected !== null}
        onClose={() => setSelected(null)}
        title={selected ? `試作 #${selected.id}` : ''}
        size="lg"
        footer={
          <div className="flex justify-between gap-2">
            <TaskyButton
              variant="outline"
              onClick={() => {
                const styleId = rows.find((r) => r.id === selected?.id)?.video_style_recipe_id;
                setSelected(null);
                if (styleId) onOpenStyle(styleId);
              }}
            >
              スタイルを開く
            </TaskyButton>
            <TaskyButton variant="ghost" onClick={() => setSelected(null)}>
              閉じる
            </TaskyButton>
          </div>
        }
      >
        {selected && (
          <div className="flex flex-col gap-2">
            {isTrialRunning(selected) && <TaskyProgress value={selected.progress} label={`作成中 ${selected.progress}%`} />}
            <VideoStyleTrialPlayer trial={selected} recipeId={rows.find((r) => r.id === selected.id)?.video_style_recipe_id} />
          </div>
        )}
      </TaskyDialog>
    </div>
  );
};

export default VideoStyleTrialsTab;
